import { inject, Injectable } from '@angular/core';
import {
  AnalyticsEvent,
  AnalyticsService,
  API_CONFIG,
  ApiCallingService,
  ApiCallingV2Service,
  ApiResponseModel,
  ResponseUtilityService
} from '@zayan-erp/shared';
import { Observable, map, tap } from 'rxjs';
import { DsmAddendumProductPayload, DsmCreateAddendumPayload } from '../models/dsm-deal-options.models';
import { DsmCreateAddendumDepositPayload } from '../models/dsm-addendum-deposit.models';
import {
  DsmSelectionBasketPreview,
  DsmSelectionBasketSavePayload,
  DsmSelectionBasketSaveResult
} from '../models/dsm-selection-basket.models';

@Injectable({ providedIn: 'root' })
export class DsmAddendumService {
  readonly #api = inject(ApiCallingService);
  readonly #apiV2 = inject(ApiCallingV2Service);
  readonly #responseHandler = inject(ResponseUtilityService);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;
  #analytics = inject(AnalyticsService);
  createAddendum(payload: DsmCreateAddendumPayload): Observable<void> {
    return this.#apiV2
      .postData<void>(`${this.baseUrl}/addendums`, { showLoader: true, showMessage: true }, payload)
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.AddendumCreated, {
            lot_id: payload.lotId,
            ...(payload.dealId ? { deal_id: payload.dealId } : {})
          });
        })
      );
  }

  addProducts(addendumId: string, payload: DsmAddendumProductPayload[]): Observable<void> {
    return this.#apiV2.postData<void>(
      `${this.baseUrl}/addendums/${addendumId}/products`,
      { showLoader: true, buttonId: 'dsm-save-options', showMessage: false, showError: true },
      payload
    );
  }

  saveSelectionBasket(payload: DsmSelectionBasketSavePayload): Observable<DsmSelectionBasketSaveResult> {
    return this.#apiV2.postData<DsmSelectionBasketSaveResult>(
      `${this.baseUrl}/addendums/selection-basket`,
      { showLoader: true, buttonId: 'dsm-save-selection-basket', showMessage: false, showError: true },
      payload
    );
  }

  previewSelectionBasket(payload: DsmSelectionBasketSavePayload): Observable<DsmSelectionBasketPreview> {
    return this.#apiV2.postData<DsmSelectionBasketPreview>(
      `${this.baseUrl}/addendums/selection-basket/preview`,
      { showLoader: false, showMessage: false, showError: false },
      payload
    );
  }

  createAddendumProductTax(
    payload: { addendumProductId: string; taxAuthorityId: string },
    showLoader: boolean = true
  ): Observable<unknown> {
    return this.#api
      .postData('addendum-products', `${payload.addendumProductId}/taxes`, payload, showLoader)
      .pipe(
        map((res: ApiResponseModel<unknown>) => this.#responseHandler.handleResponse<unknown>(res, res.message, true))
      );
  }

  deleteAddendumProductTax(addendumProductId: string, id: string, showLoader: boolean = true): Observable<void> {
    return this.#api
      .deleteData('addendum-products', `${addendumProductId}/taxes/${id}`, showLoader)
      .pipe(map((res: ApiResponseModel<void>) => this.#responseHandler.handleResponse(res, 'Tax deleted', true)));
  }

  createAddendumDeposit(addendumId: string, payload: DsmCreateAddendumDepositPayload): Observable<unknown> {
    return this.#apiV2.postData<unknown>(
      `${this.baseUrl}/deposits/addendum/${addendumId}/deposit-with-payment`,
      { showLoader: true, showMessage: true },
      payload
    );
  }
}
